import {useHitCallback, useParamsCallback, useReachGoalCallback, useTrackGACallback} from '@logistics-frontend/blocks'
import {GAGoal} from '@logistics-frontend/blocks/metrika/types'
import {
  AuthStatus,
  ProxyApiHeader,
  RequirementOptionValue,
  StartupResponse,
  StartupResponseHeaders,
} from '@yandex-taxi/js-integration-api'
import {useAuthorizationState, useCallbackRef, useEstimateState, useTariff} from '@yandex-taxi/react-integration-api'
import {useCallback, useEffect} from 'react'

import {toPascalCase} from '../../components/utils'
import {AddressType, AddressValue, PassportType} from './types'
import {addPoint, address, orderButton, orderSuccess, payment, setUserID, tariff} from './utils'

const gaDeliveryOrderAction: Pick<GAGoal, 'eventCategory' | 'eventAction'> = {
  eventCategory: 'Delivery',
  eventAction: 'Order',
}

// успешная авторизация
export const useAuthorizationGoal = (): void => {
  const reachGoal = useReachGoalCallback()
  const trackGA = useTrackGACallback()
  const params = useParamsCallback()
  const {data, meta} = useAuthorizationState<StartupResponse, StartupResponseHeaders>()
  const status = data?.authorization_status
  const userID = meta?.[ProxyApiHeader.USER_ID]

  useEffect(() => {
    if (status === AuthStatus.AUTHORIZED && userID) {
      params(setUserID(userID))
      reachGoal('DeliveryOrderLogin')
      trackGA({
        ...gaDeliveryOrderAction,
        eventLabel: 'Login',
      })
    }
  }, [params, reachGoal, trackGA, status, userID])
}
// клик по кнопке "Добавить адрес доставки"
export const useAddPointsGoalCallback = () => {
  const params = useParamsCallback()

  return useCallback(() => params(addPoint()), [params])
}
// изменение тарифа
export const useTariffGoal = (value?: string): void => {
  const params = useParamsCallback()

  useEffect(() => {
    if (value) {
      params(tariff(value))
    }
  }, [params, value])
}
// потеря фокуса на инпутах маршрута
const getAddressType = (index: number, length: number): AddressType | void => {
  switch (true) {
    case index === 0:
      return 'OrderFrom'
    case index === length - 1:
      return 'OrderTo'
  }
}
export const useAddressGoalCallback = (index: number, length: number) => {
  const params = useParamsCallback()
  const type = getAddressType(index, length)

  return useCallback(
    (value: AddressValue) => {
      if (type) {
        // отправляем в метрику только точки А и Б, остальные игнорируем
        params(address(type, value))
      }
    },
    [params, type],
  )
}
// изменение способа оплаты
export const usePaymentGoalCallback = () => {
  const params = useParamsCallback()

  return useCallback(() => params(payment()), [params])
}
// клик по кнопке "Рассчитать стоимость" в маленькой форме
export const useOrderLinkGoalCallback = () => {
  const reachGoal = useReachGoalCallback()
  const trackVirtualPage = useHitCallback()

  return useCallback(() => {
    reachGoal('DeliveryOrderEstimate')
    trackVirtualPage('/DeliveryOrderEstimate')
  }, [reachGoal, trackVirtualPage])
}
// изменение требования
export const useRequirementGoalCallback = (tariffClass: string, name: string) => {
  const params = useParamsCallback()

  return useCallback(
    (value: RequirementOptionValue) => {
      params(
        tariff(tariffClass, {
          [toPascalCase(name)]: toPascalCase(`${value}`),
        }),
      )
    },
    [name, params, tariffClass],
  )
}
// клик по кнопкам "Зарегистрироваться и продолжить", "Подтвердить номер телефона", "Привязать карту"
export const usePassportGoalCallback = (type: PassportType) => {
  const reachGoal = useReachGoalCallback()
  const trackGA = useTrackGACallback()

  return useCallback(() => {
    reachGoal('DeliveryOrderTry', orderButton(type))
    trackGA({
      ...gaDeliveryOrderAction,
      eventLabel: 'Try',
    })
  }, [reachGoal, trackGA, type])
}
// клик по ссылке "Заказать по телефону" в грузчиках
export const usePhoneGoalCallback = () => {
  const reachGoal = useReachGoalCallback()
  return useCallback(() => reachGoal('DeliveryOrderPhone'), [reachGoal])
}
// заказ создан успешно
export const useSuccessOrderGoalCallback = () => {
  const reachGoal = useReachGoalCallback()
  const trackVirtualPage = useHitCallback()
  const trackGA = useTrackGACallback()
  const {data} = useEstimateState()
  const [{value: tariff}] = useTariff()

  return useCallbackRef(() => {
    const serviceLevel = data?.service_levels?.find(s => s.class === tariff)
    const price = serviceLevel?.price_raw
    const time = serviceLevel?.time_raw
    const params = price !== undefined && time !== undefined ? orderSuccess(price, time) : undefined

    reachGoal('DeliveryOrderSuccess', params)
    trackGA({
      ...gaDeliveryOrderAction,
      eventLabel: 'Success',
    })
    trackVirtualPage('/DeliveryOrderSuccess')
  })
}
export const useInvalidOrderGoalCallback = () => {
  const reachGoal = useReachGoalCallback()
  return useCallback(() => reachGoal('Order-Button-ClickOrderNonSuccess'), [reachGoal])
}
